import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProfitByPeriodApiResponse } from '../types';
import './index.css';
import moment from 'moment';
import {
  DomainTuple,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryZoomContainer,
} from 'victory';
import { colors } from '../../../../colors';
import { ComponentDimensions } from '../../../../types/Layout';
import { getTimeFrameIntervalTimestamp } from '../PriceChart/helpers';
import { TimeFrame } from '../../../../types/Bot';

interface Props {
  data: ProfitByPeriodApiResponse | undefined;
}

type ChartDataPoint = {
  x: number;
  y: number;
  startBalance: number;
  endBalance: number;
  color?: string;
  text?: string;
};

enum Period {
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}

enum AssetType {
  Stable = 'Stable',
  Asset = 'Asset',
}

/** OVERVIEW COMPONENT
 * We receive an array of dataPoints
 * We want to show a barchart of the profit of each month (or week or year)
 * We want to loop over the dataPoints
 * Per month, we want to calculate the start and the end
 * On the chart, we show the label 'May' on the x-axis
 * This indicates the profit we have at the end of the month May
 * We want to show the actual profit as a label above the bar
 * Every bar will be pointed upwards, but the color will be red or green, depending on the profit
 *
 * We might not have the exact balance on the 1st of May,
 * but we can calculate that exact point in between
 *
 * The width of this chart will be limited
 * So we have the cut the view of and make it scrollable
 * We will always show the right side of the chart first
 */

const PeriodProfitChart: React.FC<Props> = (props: Props) => {
  const { data } = props;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  const [zoomX, setZoomX] = useState<DomainTuple | undefined>();
  const [period, setPeriod] = useState(Period.Monthly);
  const [assetType, setAssetType] = useState(AssetType.Stable);

  const unitOfTime = useMemo(
    () =>
      period === Period.Yearly
        ? 'year'
        : period === Period.Monthly
        ? 'month'
        : 'week',
    [period],
  );

  const calculateStartTimestamp = useCallback(
    (timestamp: number) => {
      return moment(timestamp).startOf(unitOfTime).valueOf();
    },
    [unitOfTime],
  );

  const calculateEndTimestamp = useCallback(
    (timestamp: number) => {
      return moment(timestamp).endOf(unitOfTime).valueOf();
    },
    [unitOfTime],
  );

  const dataPoints = useMemo(() => {
    const dataSet = assetType === AssetType.Stable ? data?.stable : data?.asset;

    if (!dataSet?.length) {
      return [];
    }

    const dataPoints: ChartDataPoint[] = [];
    let currentEndTimestamp = 0;

    for (let i = 0; i < dataSet.length; i++) {
      const datum = dataSet[i];

      if (i === 0) {
        dataPoints.push({
          x: calculateStartTimestamp(datum.timestamp),
          y: 0,
          startBalance: datum.value,
          endBalance: datum.value,
        });
        currentEndTimestamp = calculateEndTimestamp(datum.timestamp);

        continue;
      }

      const endOfMonth = calculateEndTimestamp(datum.timestamp);

      if (endOfMonth > currentEndTimestamp) {
        const latestDataPoint = dataPoints[dataPoints.length - 1];
        const previousDatum = dataSet[i - 1];

        const diff = datum.timestamp - previousDatum.timestamp;
        const a = currentEndTimestamp - previousDatum.timestamp;
        const percentA = a / diff;

        const diffValue = datum.value - previousDatum.value;
        const endOfMonthValue = previousDatum.value + diffValue * percentA;

        latestDataPoint.endBalance = endOfMonthValue;
        latestDataPoint.y = parseFloat(
          (
            ((endOfMonthValue - latestDataPoint.startBalance) /
              latestDataPoint.startBalance) *
            100
          ).toFixed(1),
        );
        latestDataPoint.text = `${latestDataPoint.y}%`;
        latestDataPoint.color =
          latestDataPoint.y >= 0 ? colors.green : colors.red;

        dataPoints.push({
          x: calculateStartTimestamp(datum.timestamp),
          y: 0,
          startBalance: endOfMonthValue,
          endBalance: endOfMonthValue,
        });
        currentEndTimestamp = endOfMonth;
      }
    }

    /** Set the current one */
    if (dataPoints.length >= 1) {
      const latestDataPoint = dataPoints[dataPoints.length - 1];
      const latestDatum = dataSet[dataSet.length - 1];

      latestDataPoint.endBalance = latestDatum.value;
      latestDataPoint.y = parseFloat(
        (
          ((latestDatum.value - latestDataPoint.startBalance) /
            latestDataPoint.startBalance) *
          100
        ).toFixed(1),
      );
      latestDataPoint.text = `${latestDataPoint.y}%`;
      latestDataPoint.color =
        latestDataPoint.y >= 0 ? colors.green : colors.red;
    }

    const values = dataPoints.map((d) => d.y);
    const avg = values.reduce((a, b) => a + Math.abs(b), 0) / values.length;

    /** This makes the gap between highest and lowest bar smaller */
    dataPoints.forEach(
      (datum) =>
        (datum.y =
          Math.abs(datum.y) === 0
            ? avg * 0.15
            : Math.abs(datum.y) + (avg - Math.abs(datum.y)) / 2),
    );

    return dataPoints;
  }, [
    assetType,
    calculateEndTimestamp,
    calculateStartTimestamp,
    data?.asset,
    data?.stable,
  ]);

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  useEffect(() => {
    if (!dataPoints) {
      return;
    }

    if (dataPoints?.length <= 4) {
      const min = Math.min(...dataPoints.map((d) => d.x));
      const max = Math.max(...dataPoints.map((d) => d.x));
      const diff = max - min;

      setZoomX([min - diff / 2.5, max + diff / 2.5]);
      return;
    }

    const now = new Date().valueOf();

    const startTimestamp =
      period === Period.Yearly
        ? moment().subtract(4.5, 'years').valueOf()
        : period === Period.Monthly
        ? now - 4.5 * getTimeFrameIntervalTimestamp(TimeFrame.OneMonth)
        : now - 4 * getTimeFrameIntervalTimestamp(TimeFrame.OneWeek);

    setZoomX([
      startTimestamp,
      now + getTimeFrameIntervalTimestamp(TimeFrame.OneWeek) * 0.55,
    ]);
  }, [calculateEndTimestamp, calculateStartTimestamp, dataPoints, period]);

  const tickValues = useMemo(() => dataPoints?.map((d) => d.x), [dataPoints]);

  const axisStyle = useMemo(
    () => ({
      tickLabels: { fontSize: 13, fill: 'rgb(200, 200, 200)' },
    }),
    [],
  );

  const formatLabel = useCallback(
    ({ datum }: { datum: ChartDataPoint }) => {
      if (!zoomX) {
        return '';
      }

      const isVisible =
        (zoomX[0] as number) <= datum.x && datum.x <= (zoomX[1] as number);

      if (!isVisible) {
        return '';
      }

      return datum.text || '';
    },
    [zoomX],
  );

  const formatDateTick = useCallback(
    (tick: number) => {
      const formatValue =
        period === Period.Yearly
          ? 'YYYY'
          : period === Period.Monthly
          ? 'MMM'
          : 'Wo';
      return moment(tick).startOf(unitOfTime).format(formatValue);
    },
    [period, unitOfTime],
  );

  const handlePeriodClick = useCallback(() => {
    setPeriod((prev) =>
      prev === Period.Yearly
        ? Period.Weekly
        : prev === Period.Monthly
        ? Period.Yearly
        : Period.Monthly,
    );
  }, []);

  const handleAssetTypeClick = useCallback(() => {
    setAssetType((prev) =>
      prev === AssetType.Stable ? AssetType.Asset : AssetType.Stable,
    );
  }, []);

  return (
    <div className="period-profit-chart-root">
      <div className="period-profit-chart-container" ref={rootRef}>
        {rootDimensions && zoomX && (
          <VictoryChart
            width={rootDimensions?.width}
            height={rootDimensions?.height}
            containerComponent={
              <VictoryZoomContainer
                zoomDimension="x"
                allowZoom={false}
                allowPan={dataPoints?.length > 4}
                zoomDomain={{ x: zoomX }}
                onZoomDomainChange={(domain) => setZoomX(domain.x)}
              />
            }
            padding={{ bottom: 30, top: 10, left: -1 }}
            domainPadding={{
              x: 6,
              y: 20,
            }}
          >
            <VictoryAxis dependentAxis tickFormat={() => ''} />
            <VictoryAxis
              style={axisStyle}
              tickValues={tickValues}
              tickFormat={formatDateTick}
            />
            <VictoryBar
              data={dataPoints}
              alignment="middle"
              style={{
                data: { fill: ({ datum }) => datum.color },
                labels: { fill: 'rgb(200, 200, 200)', fontSize: 13 },
              }}
              cornerRadius={4}
              labels={formatLabel}
              labelComponent={<VictoryLabel dy={-8} />}
              barWidth={12}
            />
          </VictoryChart>
        )}
      </div>
      {rootDimensions && zoomX && (
        <div className="period-profit-chart-toggles">
          <div
            className="period-profit-chart-toggle"
            onClick={handleAssetTypeClick}
          >
            <span className="period-profit-chart-toggle-text">{assetType}</span>
          </div>
          <div
            className="period-profit-chart-toggle"
            onClick={handlePeriodClick}
          >
            <span className="period-profit-chart-toggle-text">{period}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodProfitChart;
